"use client";
import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  TextField,
} from "@mui/material";
import { useRouter } from "next/navigation";
import Image from "next/image";
import Head from "next/head";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HomeIcon from "@mui/icons-material/Home";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import "../app/globals.css";
import { IMAGES } from "@/constants/images";

/**
 * Custom 404 Error Page for SEO Optimization
 *
 * This page helps with SEO by:
 * - Keeping users on the website
 * - Providing relevant navigation options
 * - Including search functionality
 * - Offering contact options
 * - Maintaining brand consistency
 */
export default function Custom404() {
  const router = useRouter();

  // Important pages to link to
  const importantPages = [
    {
      title: "AI Recruiting Software",
      description: "Best AI-powered recruitment tool",
      link: "/",
      icon: "🤖",
    },
    {
      title: "Zayn AI Recruiter",
      description: "24/7 AI interviewer in 50+ languages",
      link: "/zayn-ai-recruiter/",
      icon: "👨‍💼",
    },
    {
      title: "Founding Members",
      description: "Join exclusive early access program",
      link: "/founding-member-of-recruitment-automation-software/",
      icon: "⭐",
    },
    {
      title: "Blog & Resources",
      description: "Latest insights on AI recruiting",
      link: "/blog/",
      icon: "📚",
    },
    {
      title: "FAQs",
      description: "Get answers to common questions",
      link: "/faq-about-ai-recruiting-software/",
      icon: "❓",
    },
    {
      title: "Contact Us",
      description: "Get in touch with our team",
      link: "/free-trial-of-ai-recruiting-tool/",
      icon: "📞",
    },
  ];

  return (
    <>
      <Head>
        <title>404 - Page Not Found | Getgeek AI Recruiting Software</title>
        <meta
          name="description"
          content="The page you're looking for doesn't exist. Explore our AI recruiting solutions, Zayn AI recruiter, and founding member program. Get help finding what you need."
        />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getgeek.ai/404" />
      </Head>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundImage: `url('${IMAGES.FOUNDING_MEMBER_BG}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          position: "relative",
          py: { xs: 2, sm: 2, md: 2 },
          pb: { xs: 2, sm: 0 },
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          border: "none",
          outline: "none",
        }}
      >
        {/* Header */}
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: { xs: 2, sm: 2, md: 2 },
            }}
          >
            {/* Logo */}
            <Box
              sx={{ display: "flex", alignItems: "center" }}
              onClick={() => router.push("/")}
              style={{ cursor: "pointer" }}
            >
              <Image
                src={IMAGES.GETGEEK_LOGO_WHITE}
                alt="Getgeek"
                width={127}
                height={32}
              />
            </Box>

            {/* Home Button */}
            <Button
              variant="contained"
              startIcon={
                <HomeIcon sx={{ fontSize: { xs: "18px", sm: "20px" } }} />
              }
              onClick={() => router.push("/")}
              sx={{
                bgcolor: "#7c3aed",
                color: "#fff",
                px: { xs: 2, sm: 3 },
                py: { xs: 0.8, sm: 1 },
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: "13px", sm: "14px" },
                "&:hover": {
                  bgcolor: "#6d28d9",
                },
              }}
            >
              Go Home
            </Button>
          </Box>
        </Container>

        {/* Main Content */}
        <Container
          maxWidth="lg"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            pb: { xs: 3, sm: 0 },
          }}
        >
          <Box sx={{ textAlign: "center", mb: { xs: 2, sm: 3 } }}>
            {/* 404 Error Message */}
            <Typography
              variant="h1"
              sx={{
                color: "#fff",
                fontSize: { xs: "70px", sm: "100px", md: "120px" },
                fontWeight: "bold",
                lineHeight: 0.8,
                mb: 1,
                textShadow: "0 4px 20px rgba(0,0,0,0.5)",
                fontFamily: "var(--font-plusjakarta)",
                mt: { xs: "14%", sm: "14%", md: "0" },
              }}
            >
              404
            </Typography>

            <Typography
              variant="h2"
              sx={{
                color: "#fff",
                fontSize: { xs: "20px", sm: "22px", md: "28px" },
                fontWeight: "bold",
                mb: { xs: 0.8, sm: 1 },
                textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                fontFamily: "var(--font-plusjakarta)",
              }}
            >
              Oops! Page Not Found
            </Typography>

            <Typography
              sx={{
                color: "#fff",
                fontSize: { xs: "13px", sm: "16px", md: "18px" },
                mb: { xs: 2, sm: 3 },
                maxWidth: { xs: "90%", sm: 500 },
                mx: "auto",
                opacity: 0.9,
                fontFamily: "var(--font-outfit)",
                px: { xs: 1, sm: 0 },
              }}
            >
              The page you're looking for doesn't exist or has been moved. Don't
              worry, we'll help you find what you need!
            </Typography>
          </Box>

          {/* Important Pages Grid */}
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography
              variant="h3"
              sx={{
                color: "#fff",
                fontSize: { xs: "18px", sm: "20px", md: "24px" },
                fontWeight: "bold",
                mb: { xs: 1.5, sm: 2 },
                textAlign: "center",
                fontFamily: "var(--font-plusjakarta)",
              }}
            >
              Explore Our AI Recruiting Solutions
            </Typography>

            {/* Mobile & Tablet: Grid Layout, Desktop: Flex Layout */}
            <Box
              sx={{
                display: { xs: "grid", lg: "flex" },
                gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr" },
                gap: { xs: 1.5, sm: 2 },
                justifyContent: { lg: "center" },
                flexWrap: { lg: "wrap" },
                px: { xs: 1, sm: 0 },
              }}
            >
              {importantPages.map((page, index) => (
                <Box
                  key={index}
                  onClick={() => router.push(page.link)}
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    borderRadius: { xs: 2, sm: 3 },
                    p: { xs: 1.5, sm: 2 },
                    cursor: "pointer",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    transition: "all 0.3s ease",
                    minWidth: { lg: "200px" },
                    flex: { lg: "0 0 auto" },
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.15)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: { xs: 0.5, sm: 1 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "16px", sm: "20px" },
                        mr: { xs: 1, sm: 1.5 },
                      }}
                    >
                      {page.icon}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: { xs: "12px", sm: "14px" },
                        fontFamily: "var(--font-outfit)",
                        lineHeight: 1.3,
                      }}
                    >
                      {page.title}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontSize: { xs: "11px", sm: "12px" },
                      fontFamily: "var(--font-outfit)",
                      lineHeight: 1.4,
                    }}
                  >
                    {page.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Contact Section */}
          <Box
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              borderRadius: 3,
              p: { xs: 2, sm: 2 },
              textAlign: "center",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              mt: { xs: 3, sm: 8 },
              mb: 0,
              borderBottom: {
                xs: "1px solid rgba(255, 255, 255, 0.2)",
                sm: "none",
              },
              boxShadow: "none",
              mx: { xs: 1, sm: 0 },
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: "#fff",
                fontSize: { xs: "15px", sm: "18px", md: "20px" },
                fontWeight: "bold",
                mb: { xs: 0.8, sm: 1 },
                fontFamily: "var(--font-plusjakarta)",
              }}
            >
              Still Can't Find What You're Looking For?
            </Typography>

            <Typography
              sx={{
                color: "rgba(255, 255, 255, 0.8)",
                fontSize: { xs: "12px", sm: "14px" },
                mb: { xs: 1.5, sm: 2 },
                fontFamily: "var(--font-outfit)",
              }}
            >
              Our team is here to help! Get in touch with us for personalized
              assistance.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: { xs: 1, sm: 1 },
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                startIcon={
                  <ContactMailIcon
                    sx={{ fontSize: { xs: "16px", sm: "18px" } }}
                  />
                }
                onClick={() =>
                  router.push("/free-trial-of-ai-recruiting-tool/")
                }
                sx={{
                  bgcolor: "#7c3aed",
                  color: "#fff",
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.8, sm: 1 },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "11px", sm: "12px" },
                  "&:hover": {
                    bgcolor: "#6d28d9",
                  },
                }}
              >
                Contact Support
              </Button>

              <Button
                variant="outlined"
                startIcon={
                  <ArrowForwardIcon
                    sx={{ fontSize: { xs: "16px", sm: "18px" } }}
                  />
                }
                onClick={() => router.push("/")}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.8, sm: 1 },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "11px", sm: "12px" },
                  "&:hover": {
                    borderColor: "#fff",
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Back to Homepage
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}
